/**
 * Activity-diagram-specific rules.
 * Pre-layout: stadium → start/end circle transformation.
 * Post-layout: delegates to shared edge routing rules.
 */

import type { IRGraph, IRNode } from '../types';
import { NodeShape } from '../types';
import { SHAPE_REGISTRY } from '../registry';
import { applyEdgeRoutingRules } from './edge-rules';

/** Pre-layout structural transformations for activity diagrams */
export function applyPreLayoutRules(graph: IRGraph): void {
  transformStadiumNodes(graph);
}

export function applyEdgeRules(graph: IRGraph): void {
  applyEdgeRoutingRules(graph);
}

// ─── Stadium → Start/End circle transformation ─────────────────

/**
 * Transform stadium-shaped nodes into UML start/end circle pairs.
 * Only runs for activity output (called from applyPreLayoutRules).
 * - Has outgoing edges → Start: StartCircle → edge → Process(label)
 * - No outgoing edges → End: Process(label) → edge → EndCircle
 */
function transformStadiumNodes(graph: IRGraph): void {
  const hasOutgoing = new Set<string>();
  for (const edge of graph.edges) {
    hasOutgoing.add(edge.source);
  }

  const stadiumIds: string[] = [];
  for (const [id, node] of graph.nodes) {
    if (node.shape === NodeShape.Stadium) {
      stadiumIds.push(id);
    }
  }

  for (const id of stadiumIds) {
    const node = graph.nodes.get(id)!;
    const circleEntry = SHAPE_REGISTRY[NodeShape.StartCircle];

    if (hasOutgoing.has(id)) {
      // --- Start node ---
      const originalLabel = node.label;
      const actionId = `${id}_action`;

      node.shape = NodeShape.StartCircle;
      node.label = '';
      node.width = circleEntry.defaultWidth;
      node.height = circleEntry.defaultHeight;

      const processEntry = SHAPE_REGISTRY[NodeShape.Process];
      const actionNode: IRNode = {
        id: actionId,
        label: originalLabel,
        shape: NodeShape.Process,
        width: processEntry.defaultWidth,
        height: processEntry.defaultHeight,
        laneId: node.laneId,
        meta: {},
      };
      graph.nodes.set(actionId, actionNode);

      for (const lane of graph.lanes) {
        if (lane.nodeIds.includes(id)) {
          lane.nodeIds.push(actionId);
          break;
        }
      }

      for (const edge of graph.edges) {
        if (edge.source === id) {
          edge.source = actionId;
        }
      }

      graph.edges.push({
        id: `e${graph.edges.length + 1}`,
        source: id,
        target: actionId,
      });
    } else {
      // --- End node ---
      const endId = `${id}_end`;

      node.shape = NodeShape.Process;

      const endEntry = SHAPE_REGISTRY[NodeShape.EndCircle];
      const endNode: IRNode = {
        id: endId,
        label: '',
        shape: NodeShape.EndCircle,
        width: endEntry.defaultWidth,
        height: endEntry.defaultHeight,
        laneId: node.laneId,
        meta: {},
      };
      graph.nodes.set(endId, endNode);

      for (const lane of graph.lanes) {
        if (lane.nodeIds.includes(id)) {
          lane.nodeIds.push(endId);
          break;
        }
      }

      graph.edges.push({
        id: `e${graph.edges.length + 1}`,
        source: id,
        target: endId,
      });
    }
  }
}
